import React, { useState } from 'react';
import { AlertTriangle, Shield, Clock, FileText, Phone, CheckSquare } from 'lucide-react';

const DisclaimerPage: React.FC = () => {
  const [acknowledged, setAcknowledged] = useState({
    timeDuration: false,
    disclaimer: false,
    confidentiality: false,
    emergencyUnderstanding: false
  });

  const handleAcknowledgment = (section: keyof typeof acknowledged) => {
    setAcknowledged(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const allAcknowledged = Object.values(acknowledged).every(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            DISCLAIMER, YOUR CONFIDENTIALITY AND YOUR CONSENT
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Please read the following important information carefully before proceeding with any health screening or assessment services.
          </p>
        </div>

        {/* Emergency Notice */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-red-800 mb-2">
                IMPORTANT SAFETY NOTICE
              </h3>
              <p className="text-red-700 font-medium">
                If you feel at risk of committing suicide or harming yourself or other people, 
                you must not continue with this screening. Please save your progress and immediately 
                contact your local Accident and Emergency Services for support. Only return to complete 
                the screening when you feel no longer at risk to yourself and to other people.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Sections */}
        <div className="space-y-8">
          
          {/* Section A: Time Duration */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-start gap-4 mb-6">
              <Clock className="h-6 w-6 text-blue-600 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  (a) Time Duration
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  I agree that the self-screening may take up to 45 minutes to one hour or more. 
                  I can commence, save and complete my self-screening whenever it suits me.
                </p>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.timeDuration}
                    onChange={() => handleAcknowledgment('timeDuration')}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">
                    I acknowledge and agree to the time duration requirements
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Section B: Disclaimer */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-start gap-4 mb-6">
              <FileText className="h-6 w-6 text-amber-600 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  (b) Disclaimer
                </h2>
                <div className="text-gray-700 leading-relaxed space-y-4 mb-6">
                  <p>
                    I agree that this is an emotional health self-screening that <strong>must not and cannot be used 
                    for self-diagnosis or court proceedings</strong>. The assessment must not replace my medical 
                    assessments and treatment with a health professional.
                  </p>
                  <p>
                    The self-screening is only one component of an assessment a health professional may undertake 
                    before providing me treatment. I will consult my family doctor for diagnosis and treatment if 
                    I am worried about my health.
                  </p>
                  <p>
                    Sometimes my health professional may prefer to conduct their own assessment for me rather than 
                    using my self-screening report. This means that it may be useful for me to ask my health 
                    professional if they want me to complete this self-screening or not.
                  </p>
                  <p>
                    <strong>Enaikidigha Ltd, Twin Brain Ltd and affiliated agencies or anyone else is not liable 
                    for my use of this assessment facility including the loss of my screening outcome report at any time.</strong>
                  </p>
                </div>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.disclaimer}
                    onChange={() => handleAcknowledgment('disclaimer')}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">
                    I understand and agree to the disclaimer terms
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Section C: Confidentiality */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-start gap-4 mb-6">
              <Shield className="h-6 w-6 text-green-600 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  (c) Confidentiality
                </h2>
                <div className="text-gray-700 leading-relaxed space-y-4 mb-6">
                  <p>
                    I agree that every information I provide during this screening and the screening outcome 
                    report are <strong>confidential and not stored or read by the personnel at Twin Brain Ltd 
                    or Enaikidigha Ltd</strong>. I am to decide who I want to share my report with.
                  </p>
                  <p>
                    It is my personal responsibility to print or email my report to myself and to delete the 
                    copy in the Twin Brain facility.
                  </p>
                  <p>
                    If I choose to keep a copy of my report under my personal account in the App or website, 
                    <strong>my report shall be automatically deleted by the App or website at the end of 12 hours 
                    from the time my report was saved</strong> by the App or the website.
                  </p>
                </div>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.confidentiality}
                    onChange={() => handleAcknowledgment('confidentiality')}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">
                    I understand and agree to the confidentiality terms
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Emergency Service Notice */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-start gap-4 mb-6">
              <Phone className="h-6 w-6 text-red-600 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Emergency Service Notice
                </h2>
                <div className="text-gray-700 leading-relaxed space-y-4 mb-6">
                  <p>
                    I agree that <strong>this App or website is not and does not provide an Emergency Service 
                    and is not a substitute to medical and professional support</strong>.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium">
                      For emergency situations, please contact:
                    </p>
                    <ul className="mt-2 text-red-700 space-y-1">
                      <li>• <strong>Emergency Services: 999</strong></li>
                      <li>• <strong>NHS 111: 111</strong></li>
                      <li>• <strong>Samaritans: 116 123</strong></li>
                      <li>• Your local Accident & Emergency department</li>
                    </ul>
                  </div>
                </div>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acknowledged.emergencyUnderstanding}
                    onChange={() => handleAcknowledgment('emergencyUnderstanding')}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">
                    I understand this is not an emergency service and agree to seek appropriate help when needed
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Summary and Consent */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Consent Summary
          </h2>
          <p className="text-gray-700 mb-6">
            By proceeding, you confirm that you have read, understood, and agree to all the terms outlined above. 
            You acknowledge the limitations of this self-screening tool and understand your responsibilities regarding 
            your health and safety.
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className={`h-6 w-6 ${allAcknowledged ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={`font-medium ${allAcknowledged ? 'text-green-700' : 'text-gray-600'}`}>
                {allAcknowledged ? 'All sections acknowledged' : 'Please acknowledge all sections above'}
              </span>
            </div>
            
            <button
              disabled={!allAcknowledged}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                allAcknowledged
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              I Consent and Agree to Proceed
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600">
          <p className="text-sm">
            This disclaimer is part of HealthCity's commitment to providing safe, ethical, and transparent health services.
          </p>
          <p className="text-sm mt-2">
            For questions about this disclaimer, please contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerPage;